// Les modules du club (D-122) : ce que l'app demande à la base. Le bureau seul active et éteint (les fonctions le
// vérifient) ; un coach demande au bureau ; tout compte du club lit l'état (son app le suit).
import { supabase } from './supabase';
import type { ModuleKey, PreviewNeed } from '../domain/modules';
import { moduleWizards, type WizardRow } from './assistant-api';

const db = () => {
  if (!supabase) throw new Error('Base non configurée');
  return supabase;
};
async function must<T = unknown>(q: PromiseLike<{ data: unknown; error: { message: string; code?: string } | null }>): Promise<T> {
  const { data, error } = await q;
  if (error) throw Object.assign(new Error(error.message), { code: error.code });
  return data as T;
}
const rpc = <T>(fn: string, args: Record<string, unknown> = {}) => must<T>(db().rpc(fn, args));

export interface ModuleRow { module: ModuleKey; enabled: boolean; at: string; origin: 'bureau' | 'donnees' | 'demo'; by: string | null }
export interface ModulesState {
  /** « Ce que le club utilise » a été validé (premiers pas). */
  chosen: boolean;
  modules: ModuleRow[];
  /** Bureau : les demandes des coachs (modules encore éteints). */
  asks: { module: ModuleKey; name: string; at: string }[];
  /** Les bêtas ouvertes à ce club (D-141 : la formation ; D-164 : « mainsvives », le jeu caméra), club pilote et démo. */
  betas?: (ModuleKey | 'mainsvives')[];
  /** D-158 : les assistants du club que je peux ouvrir (en cours, ou finis avec des rubriques « à revoir »). */
  wizards?: WizardRow[];
}
/** L'état des modules d'un club, ses bêtas (lues à part : club_beta_features) et ses assistants (D-158). */
export async function clubModules(clubId: string): Promise<ModulesState | null> {
  const [st, betas, wizards] = await Promise.all([rpc<ModulesState | null>('club_modules', { p_club: clubId }),
    rpc<(ModuleKey | 'mainsvives')[] | null>('club_beta_features', { p_club: clubId }).catch(() => [] as ModuleKey[]),
    moduleWizards(clubId).catch(() => [] as WizardRow[])]);
  return st ? { ...st, betas: betas ?? [], wizards } : st;
}
/** La console de l'éditeur (C1) : les modules d'un club et les demandes de ses coachs. */
export const consoleClubModules = (clubId: string) =>
  rpc<{ modules: ModuleRow[]; asks: { module: ModuleKey; name: string; at: string }[] }>('console_club_modules', { p_club: clubId });

export interface ModulePreview {
  module: ModuleKey; enabled: boolean;
  notice: { title: string; body: string } | null;
  // bénévolat
  home_matches?: number; needs?: PreviewNeed[]; signed?: number; first?: { name: string; label: string; date: string } | null;
  // avantages
  rewards_missing?: string[];
  // page
  slug?: string | null; online?: boolean; followers?: number;
  // agenda
  events?: number;
}
export const modulePreview = (clubId: string, key: ModuleKey) => rpc<ModulePreview>('club_module_preview', { p_club: clubId, p_module: key });

export interface SetResult { changed: boolean; matches?: number; notified?: boolean; rewards?: boolean; online?: boolean }
export const setModule = (clubId: string, key: ModuleKey, on: boolean, notify = false, options: { rewards?: boolean } = {}) =>
  rpc<SetResult>('club_module_set', { p_club: clubId, p_module: key, p_on: on, p_notify: notify, p_options: options });
/** « Ce que le club utilise » (premiers pas) : exactement ces modules. */
export const chooseModules = (clubId: string, keys: ModuleKey[]) => rpc<{ on: number }>('club_modules_choose', { p_club: clubId, p_modules: keys });
/** Les récompenses des grades proposées (Avantages › gestion) : le bureau les reprend ; rend le nombre créé. */
export const adoptGradeRewards = (clubId: string) => rpc<number>('grade_rewards_adopt', { p_club: clubId });
/** « Demander au bureau » (coach) : true si la demande part (une par semaine au plus). */
export const askModule = (clubId: string, key: ModuleKey) => rpc<boolean>('club_module_ask', { p_club: clubId, p_module: key });

export interface Kept { slots: { label: string; title: string; date: string }[]; events: { title: string; starts_at: string; location: string | null }[] }
/** Ce que je garde d'un module éteint jusqu'à la date (mon créneau, mon inscription). */
export const keptCommitments = (clubId: string) => rpc<Kept | null>('my_kept_commitments', { p_club: clubId });

/** Côté joueuses (D-122) : « Plays » n'apparaît qu'au premier play publié de leurs équipes (contenu, pas module). */
export async function hasPublishedPlay(teamIds: string[]): Promise<boolean> {
  if (!teamIds.length) return false;
  const { count, error } = await db().from('plays').select('id', { count: 'exact', head: true }).in('team_id', teamIds).eq('published', true);
  if (error) return true;   // dans le doute, l'onglet reste
  return (count ?? 0) > 0;
}
